// ============================================================
// Fee Structure Item Service
// ============================================================

import { feeStructureItemsApi } from '@/api/feeStructureItems.api';
import { generateId } from '@/utils/generateId';

// In-memory mock storage fallback for local testing offline
const initialMockItems = {};

export const feeStructureItemService = {
  getItems: async (feeStructureId) => {
    if (!feeStructureId) return [];
    try {
      const res = await feeStructureItemsApi.getItems(feeStructureId);
      return Array.isArray(res) ? res : res?.items || res?.data || [];
    } catch (err) {
      console.warn('API error fetching fee structure items, using mock fallback:', err.message);
      return initialMockItems[feeStructureId] || [];
    }
  },

  createItem: async (feeStructureId, payload) => {
    if (!feeStructureId) throw new Error('Fee Structure ID is required');
    try {
      const res = await feeStructureItemsApi.createItem(feeStructureId, payload);
      const itemId = res?.item_id || res?.id || res?.data?.id || res?.data?.item_id;
      return {
        ...res,
        success: true,
        item_id: itemId,
        id: itemId,
        message: res?.message || 'Fee structure item created'
      };
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to create fee item';
        throw new Error(errorMsg);
      }
      console.warn('API error creating fee structure item, saving locally:', err.message);
      const newItem = {
        id: generateId('fsi-'),
        fee_structure_id: feeStructureId,
        due_day: 10,
        due_month: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...payload,
      };
      if (!initialMockItems[feeStructureId]) {
        initialMockItems[feeStructureId] = [];
      }
      initialMockItems[feeStructureId].push(newItem);
      return { success: true, item_id: newItem.id, message: 'Fee structure item created' };
    }
  },

  updateItem: async (itemId, payload) => {
    if (!itemId) throw new Error('Item ID is required');
    try {
      const res = await feeStructureItemsApi.updateItem(itemId, payload);
      return { success: true, item_id: res?.item_id || itemId, message: res?.message || 'Fee structure item updated' };
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to update fee item';
        throw new Error(errorMsg);
      }
      console.warn('API error updating fee structure item:', err.message);
      return { success: true, item_id: itemId, message: 'Fee structure item updated' };
    }
  },

  deleteItem: async (itemId) => {
    if (!itemId) throw new Error('Item ID is required');
    try {
      const res = await feeStructureItemsApi.deleteItem(itemId);
      return { success: true, message: res?.message || 'Fee structure item deleted successfully.' };
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to delete fee item';
        throw new Error(errorMsg);
      }
      console.warn('API error deleting fee structure item:', err.message);
      return { success: true, message: 'Fee structure item deleted successfully.' };
    }
  },
};
